/**
 * TenantStatusBanner — full-screen overlay když API vrátí 503 s
 * ``X-Tenant-Status: suspended`` nebo ``deleted``.
 *
 * PR-30 (Design A+B): backend middleware ``TenantStatusMiddleware``
 * 503-uje admin endpointy pro suspended/deleted tenanty. Whitelist
 * ponechává auth + tracking + quote view fungující, takže suspended
 * uživatel se může přihlásit a vidět tracking links. Ale jakmile
 * jejich SPA zavolá první admin/data endpoint a dostane 503, tahle
 * komponenta zobrazí "tenant pozastaven" banner přes celý viewport.
 *
 * Detekce přes ``ApiError.code`` (= "tenant_suspended" / "tenant_deleted")
 * z middleware response body. ApiError nemá direct access k headers,
 * ale code v response body je dost.
 *
 * Vzhled: Claude Design „Pozastavený shop", varianta F (KAN-1334).
 *
 * KAN-1335: „Zapnout zpět" se ukáže JEN když 503 nese
 * ``self_service_resume: true`` (pozastaveno za neplacení). Vede na
 * ``POST /billing/reactivate`` → Stripe portál; obchod zapne až webhook
 * o zaplacení, ne kliknutí. Jiný důvod (ToS, ruční suspend) = jen kontakt
 * podpory. Rozhoduje backend, FE si pravidlo nedrží.
 *
 * KAN-1336: meta řádek „Pozastaveno" / „Smazáno" / „Data dostupná do"
 * jen z polí, která backend poslal. Pozastavený tenant žádnou lhůtu
 * dostupnosti dat nemá, takže ji backend neposílá a tu se nekreslí.
 *
 * „Napsat podpoře" vede na kontaktní formulář PLATFORMY, ne tenanta:
 * overlay běží na hostu shopu, jehož vlastní /contact by zprávu doručil
 * právě tomu pozastavenému / smazanému shopu.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { apiFetch } from "@/lib/api";
import { buildPlatformUrl } from "@/shell/resolveShell";

import "./TenantStatusBanner.css";

export interface TenantStatusContext {
  /** "suspended" nebo "deleted" (z ApiError.code parse). */
  status: "suspended" | "deleted";
  /** Slug tenanta (z error.params, pokud k dispozici). */
  slug?: string;
  /** KAN-1335: smí tenant obchod zapnout sám (zaplacením)? */
  selfServiceResume?: boolean;
  /** KAN-1336: ISO okamžik pozastavení / smazání a konec dostupnosti dat. */
  suspendedAt?: string;
  deletedAt?: string;
  purgeAfter?: string;
}

function formatDay(iso: string, lang: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function reactivatePath(slug: string | undefined): string {
  // Bez slugu platí host shopu (subdoména / vlastní doména) — middleware
  // ho rozliší sám. Se slugem jde explicitní /o/{slug} prefix.
  const suffix = "/api/v1/billing/reactivate";
  return slug ? `/o/${slug}${suffix}` : suffix;
}

export function TenantStatusBanner({
  context,
}: {
  context: TenantStatusContext;
}): JSX.Element {
  const { t, i18n } = useTranslation("admin");
  const lang = isSupportedLang(i18n.language) ? i18n.language : DEFAULT_LANG;
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function onReactivate(): Promise<void> {
    if (pending) return;
    setPending(true);
    setFailed(false);
    try {
      const { url } = await apiFetch<{ url: string }>(
        reactivatePath(context.slug),
        { method: "POST" },
      );
      window.location.href = url;
    } catch {
      setFailed(true);
      setPending(false);
    }
  }

  const meta: { key: string; label: string; value: string }[] = [];
  const pushDate = (key: string, iso: string | undefined) => {
    const value = iso ? formatDay(iso, lang) : null;
    if (value) meta.push({ key, label: t(`tenantStatus.${key}`), value });
  };
  pushDate("suspendedAt", context.suspendedAt);
  pushDate("deletedAt", context.deletedAt);
  pushDate("purgeAfter", context.purgeAfter);

  return (
    <div
      data-testid="tenant-status-banner"
      data-status={context.status}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="tenant-status-heading"
      className="tsb fixed inset-0"
    >
      <div className="tsb-tape" aria-hidden="true" />
      <div className="tsb-panel">
        <div className="tsb-badge">
          <span className="tsb-dot" aria-hidden="true" />
          {t(`tenantStatus.${context.status}.badge`)}
        </div>
        <h2 id="tenant-status-heading" className="t-h2">
          {t(`tenantStatus.${context.status}.heading`)}
        </h2>
        <p className="t-body">{t(`tenantStatus.${context.status}.body`)}</p>
        <div className="btn-group">
          {context.status === "suspended" && context.selfServiceResume && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onReactivate()}
              disabled={pending}
              data-testid="tenant-status-reactivate"
            >
              {t("tenantStatus.reactivate")}
            </button>
          )}
          <a
            className="btn btn-secondary"
            href={buildPlatformUrl(`/${lang}/contact`)}
            data-testid="tenant-status-support"
          >
            {t("tenantStatus.contactSupport")}
          </a>
        </div>
        {failed && (
          <p className="tsb-error" role="alert" data-testid="tenant-status-reactivate-error">
            {t("tenantStatus.reactivateFailed")}
          </p>
        )}
        {(context.slug || meta.length > 0) && (
          <div className="tsb-meta">
            {context.slug && (
              <div>
                <span>{t("tenantStatus.organization")}</span>
                <span className="mono">{context.slug}</span>
              </div>
            )}
            {meta.map((m) => (
              <div key={m.key} data-testid={`tenant-status-${m.key}`}>
                <span>{m.label}</span>
                <span className="mono">{m.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Try to derive a tenant-status context z ApiError. Vrací null kdy se
 * nejedná o tenant-suspension odpověď.
 */
export function tenantStatusFromError(
  err: { status: number; code: string; params: Record<string, unknown> },
): TenantStatusContext | null {
  if (err.status !== 503) return null;
  let status: TenantStatusContext["status"];
  if (err.code === "tenant_suspended") status = "suspended";
  else if (err.code === "tenant_deleted") status = "deleted";
  else return null;
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v ? v : undefined;
  const p = err.params;
  return {
    status,
    slug: str(p.slug),
    selfServiceResume: p.self_service_resume === true,
    suspendedAt: str(p.suspended_at),
    deletedAt: str(p.deleted_at),
    purgeAfter: str(p.purge_after),
  };
}
